import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * StateOfFirmSubscribe \u2014 restrained, single-field opt-in for the
 * quarterly "State of the Firm" print letter and its LinkedIn adaptation.
 * Distinct from the Journal subscribe (which covers essays). Posts to the
 * existing /api/subscribe endpoint with source: "state_of_the_firm".
 *
 * Two visual modes:
 *   variant="dark"  \u2014 for ivory/light backgrounds (used on /contact)
 *   variant="ivory" \u2014 for navy panels (not currently used)
 */
export default function StateOfFirmSubscribe({ variant = "dark", className = "" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    try {
      const { data } = await api.post("/subscribe", {
        email: email.trim(),
        source: "state_of_the_firm",
      });
      setState("done");
      if (data.already_subscribed) {
        toast.success("You are already on the mailing list.");
      } else {
        toast.success("Check your inbox — one click confirms.");
      }
      setEmail("");
      setTimeout(() => setState("idle"), 6000);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add you to the list"
      );
      setState("idle");
    }
  };

  const isDark = variant === "dark";

  return (
    <section
      className={`${isDark ? "bg-linen" : "panel-navy grain grain-dark"} py-16 md:py-20 ${className}`}
      data-testid="sof-subscribe"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className={`text-[10px] uppercase tracking-[0.36em] font-medium ${isDark ? "text-brass" : "text-brass"}`}>
              A Quarterly Dispatch
            </div>
            <h3
              className={`mt-4 font-display text-3xl md:text-4xl leading-[1.05] ${isDark ? "text-navy" : "text-ivory"}`}
            >
              The State of the Firm,{" "}
              <span className="italic font-cormorant text-oxblood">by post.</span>
            </h3>
            <p className={`mt-5 max-w-xl font-cormorant italic text-lg leading-snug ${isDark ? "text-slate2" : "text-ivory/85"}`}>
              A signed letter, four times a year, from the Managing Partner. Firm
              developments, industry notes, and the counsel we are preparing to keep.
              Sent by post and adapted for LinkedIn.
            </p>
          </div>

          <form
            className="lg:col-span-5 flex flex-col gap-3"
            onSubmit={submit}
            data-testid="sof-subscribe-form"
          >
            <label
              className={`text-[10px] uppercase tracking-[0.32em] font-medium ${isDark ? "text-slate2/70" : "text-ivory/60"}`}
              htmlFor="sof-email"
            >
              Add me to the mailing list
            </label>
            <input
              id="sof-email"
              type="email"
              required
              placeholder="your.email@firm.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={
                isDark
                  ? "field !mb-0"
                  : "field !mb-0 !bg-transparent !text-ivory !border-ivory/30 placeholder:text-ivory/40 focus:!border-brass"
              }
              data-testid="sof-subscribe-email"
              disabled={state === "sending"}
            />
            <button
              type="submit"
              className="btn-brass self-start inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="sof-subscribe-submit"
              disabled={state === "sending"}
            >
              <span className="text-lg leading-none">&#9733;</span>
              {state === "sending"
                ? "Sending confirmation\u2026"
                : state === "done"
                  ? "Check your inbox"
                  : "Add me to the list"}
            </button>
            <div
              className={`text-[10px] uppercase tracking-[0.24em] ${isDark ? "text-slate2/60" : "text-ivory/45"}`}
            >
              Four issues a year. Never sold. Unsubscribe by reply.
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
